import { FC, useMemo, useState } from "react";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading/loading";
import __ from "web/Layout/Translations";

import ArrowRightIcon from "web/assets/icons/arrow-right.svg";

import { trackCategoryExpand } from "web/utils/system/GTM/navigationGtmEvents";

import type { ICategory } from "web/types/Category";
import type { ICategoryCount } from "web/types/CategoryCount";
import type { Nullable } from "web/types/Utils";

import CategoryListItem from "./CategoryListItem";
import SubcategoryView from "./SubcategoryView";
import SubmenuView from "./SubmenuView";
import classes from "./list.scss";
import NavItem from "./navItem";
import type { Submenu } from "./utils/types";
import useHandleListNavigation from "./utils/useHandleListNavigation";
import useNavigationData from "./utils/useNavigationData";

interface INavigationWebViewListProps {
  rootCategoryId: number;
  categoriesTree?: string[];
  categoriesData: ICategory[];
  categoriesSizes: ICategoryCount[];
}

const NavigationWebViewList: FC<INavigationWebViewListProps> = ({
  rootCategoryId,
  categoriesTree,
  categoriesData,
  categoriesSizes,
}) => {
  const [submenu, setSubmenu] = useState<Nullable<Submenu>>(null);

  const {
    startCategoryData,
    categoryListData,
    processedMenuItems,
    isLoading,
    isError,
  } = useNavigationData({
    categoriesTree,
    categoriesData,
    categoriesSizes,
  });

  const {
    handleCategoryClick,
    handleSubmenuClick,
    handleNavigate,
    handleBack,
  } = useHandleListNavigation({
    setSubmenu,
  });

  const parentLabel = startCategoryData?.name || submenu?.title;

  const categoryListItems = useMemo(
    () =>
      categoryListData.map((category) => (
        <CategoryListItem
          key={category.id}
          category={category}
          parentLabel={parentLabel}
          onClick={() =>
            handleCategoryClick(category.id, category.hasSubcategory)
          }
        />
      )),
    [categoryListData, handleCategoryClick, parentLabel]
  );

  const additionalList = useMemo(
    () =>
      processedMenuItems.map((item) => {
        if (item.submenu) {
          return (
            <button
              type="button"
              key={item.code}
              className={classes.navItem}
              onClick={() => {
                trackCategoryExpand(item.title);
                handleSubmenuClick(item.title, item.submenu || []);
              }}
            >
              <span>{__(item.title)}</span>
              <ArrowRightIcon width={10} />
            </button>
          );
        }

        if (item.url) {
          return (
            <NavItem key={item.code} to={item.url} title={__(item.title)} />
          );
        }

        return null;
      }),
    [processedMenuItems, handleSubmenuClick]
  );

  const allListItems = useMemo(
    () =>
      startCategoryData
        ? categoryListItems
        : [...categoryListItems, ...additionalList],
    [startCategoryData, categoryListItems, additionalList]
  );

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorComponent />;
  }
  if (startCategoryData) {
    return (
      <SubcategoryView
        startCategoryData={startCategoryData}
        rootCategoryId={rootCategoryId}
        listItems={allListItems}
        onBack={submenu ? () => handleBack(null) : handleBack}
        onNavigate={handleNavigate}
      />
    );
  }

  if (submenu) {
    return (
      <SubmenuView
        submenu={submenu}
        onBack={() => setSubmenu(null)}
        onNavigate={handleNavigate}
        onCategoryClick={handleCategoryClick}
      />
    );
  }

  return <div className={classes.categoryRoot}>{allListItems}</div>;
};

export default NavigationWebViewList;
